import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Zap, Shield, Settings, ChevronRight, CheckCircle,
  Play, Pause, Eye, RotateCcw, GitBranch, Clock, AlertTriangle,
  FileText, Users, Lock, Unlock, HelpCircle, History, Layers
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { MarketingLayout } from '../../components/marketing/MarketingLayout';

const rolloutLadder = [
  {
    level: 'Observe',
    icon: Eye,
    description: 'System monitors existing processes and logs what would have been automated. No changes made.',
    actions: ['Log potential automations', 'Measure baseline metrics', 'Identify patterns', 'Build confidence']
  },
  {
    level: 'Propose',
    icon: Pause,
    description: 'System proposes actions for human review before execution. Operator can approve, modify, or reject.',
    actions: ['Generate action proposals', 'Present with full context', 'Await human approval', 'Learn from rejections']
  },
  {
    level: 'Approve',
    icon: CheckCircle,
    description: 'System executes after approval from designated approvers. Approval chains based on risk level.',
    actions: ['Route to approvers', 'Enforce approval chains', 'Execute on approval', 'Log decisions']
  },
  {
    level: 'Auto',
    icon: Zap,
    description: 'System executes automatically within policy boundaries. Exceptions escalate to human operators.',
    actions: ['Execute within policy', 'Escalate exceptions', 'Continuous monitoring', 'Automatic rollback']
  }
];

const templates = [
  {
    name: 'New Hire Onboarding',
    domain: 'Identity',
    steps: 8,
    description: 'Provision IdP accounts, assign device profiles, grant app access, notify stakeholders.',
    integrations: ['Okta', 'Intune', 'Google Workspace', 'Slack']
  },
  {
    name: 'Employee Offboarding',
    domain: 'Identity',
    steps: 12,
    description: 'Revoke sessions, disable accounts, transfer data, reclaim licenses, update HRIS.',
    integrations: ['Okta', 'Microsoft 365', 'Salesforce', 'BambooHR']
  },
  {
    name: 'Privileged Access Request',
    domain: 'Access',
    steps: 6,
    description: 'Request, approve, provision time-bound elevated access with enhanced monitoring.',
    integrations: ['Okta', 'AWS IAM', 'GitHub']
  },
  {
    name: 'Malware Containment',
    domain: 'Security',
    steps: 7,
    description: 'Isolate endpoint, revoke sessions, block network access, collect forensics, notify team.',
    integrations: ['CrowdStrike', 'Okta', 'Intune', 'Slack']
  },
  {
    name: 'License Reclamation',
    domain: 'SaaS',
    steps: 5,
    description: 'Identify inactive users, notify managers, revoke unused licenses, update billing.',
    integrations: ['Salesforce', 'Zoom', 'Figma', 'Notion']
  },
  {
    name: 'Device Compliance Remediation',
    domain: 'Endpoints',
    steps: 4,
    description: 'Detect non-compliant device, notify user, enforce policy, escalate if unresolved.',
    integrations: ['Intune', 'Jamf', 'Slack']
  }
];

const policyStudioFeatures = [
  {
    icon: Shield,
    title: 'Policy Definition',
    description: 'Define when automation can execute. Conditions based on user attributes, request type, risk level, and organizational rules.'
  },
  {
    icon: Play,
    title: 'Simulation',
    description: 'Test policies against historical data before deployment. See what would have been approved or blocked.'
  },
  {
    icon: GitBranch,
    title: 'Version Control',
    description: 'Track all policy changes with full audit trail. Compare versions. Rollback to previous states.'
  },
  {
    icon: AlertTriangle,
    title: 'Impact Analysis',
    description: 'Before deploying policy changes, see which active cases and future requests would be affected.'
  }
];

const actionBundleFeatures = [
  {
    title: 'Composable Actions',
    description: 'Build workflows from pre-built action primitives. Each action handles a specific integration task.'
  },
  {
    title: 'Conditional Logic',
    description: 'Branch based on data from previous steps or external lookups. Handle different scenarios in one bundle.'
  },
  {
    title: 'Error Handling',
    description: 'Define what happens when actions fail. Retry logic, fallback actions, and escalation paths.'
  },
  {
    title: 'Evidence Capture',
    description: 'Every action captures before/after state. Evidence packs generated automatically for compliance.'
  }
];

const versioningFeatures = [
  { feature: 'Change History', description: 'Every edit tracked with author, timestamp, and change description' },
  { feature: 'Version Comparison', description: 'Side-by-side diff of any two versions' },
  { feature: 'Staged Rollout', description: 'Deploy to subset of cases before full rollout' },
  { feature: 'Instant Rollback', description: 'Revert to any previous version with one click' },
  { feature: 'Audit Trail', description: 'Full record of who changed what and why' }
];

const faqs = [
  {
    question: 'How do action bundles differ from traditional runbooks?',
    answer: 'Action bundles are executable workflows, not documentation. Each step is a real integration action with inputs, outputs, and error handling. They capture evidence automatically and respect policy gates.'
  },
  {
    question: 'Can I create custom actions for our internal systems?',
    answer: 'Yes. The Connector SDK allows you to build custom actions for any system with an API. Custom actions appear in the bundle builder alongside built-in actions.'
  },
  {
    question: 'How are policy conflicts resolved?',
    answer: 'Policies are evaluated in priority order. The most restrictive matching policy applies. Conflict detection during authoring warns when new policies might override existing ones.'
  },
  {
    question: 'What happens when an action fails mid-bundle?',
    answer: 'Depends on configuration. Options include: retry with backoff, execute fallback action, rollback completed steps, pause for human intervention, or continue with degraded state.'
  },
  {
    question: 'How do I migrate from observe to auto mode?',
    answer: 'Gradual rollout is built in. Start with observe to build confidence, then move to propose for human validation, then approve for explicit sign-off, finally auto for full automation. Each step provides metrics to inform the next.'
  }
];

export const AutomationBuilder = () => {
  return (
    <MarketingLayout>
      <div className="min-h-screen">
        {/* Hero */}
        <section className="py-16 px-6 bg-white border-b border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Link to="/product" className="hover:text-foreground">Product</Link>
              <ChevronRight className="w-4 h-4" />
              <span>Automation Builder</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 tracking-tight">
              Action Bundles & Policy Studio
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-3xl leading-relaxed">
              Build automation workflows with Action Bundles. Govern execution with Policy Studio.
              Rollout gradually from observe to full automation.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button asChild size="default">
                <Link to="/demo/request">
                  Request Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="default" variant="outline">
                <Link to="/policy-studio">Open Policy Studio</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Rollout Ladder */}
        <section className="py-16 px-6 bg-secondary/30">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-2">Rollout Ladder</h2>
            <p className="text-muted-foreground mb-8">Gradual progression from observation to full automation.</p>
            <div className="grid md:grid-cols-4 gap-4">
              {rolloutLadder.map((level, idx) => (
                <div key={idx} className="relative">
                  <div className="bg-white border border-border rounded-lg p-5 h-full">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        idx === 3 ? 'bg-primary text-primary-foreground' : 'bg-primary/10'
                      }`}>
                        <level.icon className={`w-5 h-5 ${idx === 3 ? '' : 'text-primary'}`} />
                      </div>
                      <h3 className="text-base font-semibold text-foreground">{level.level}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4">{level.description}</p>
                    <ul className="space-y-2">
                      {level.actions.map((action, aidx) => (
                        <li key={aidx} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <CheckCircle className="w-3 h-3 text-primary" />
                          {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {idx < rolloutLadder.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-2 w-4 text-muted-foreground">
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Action Bundles */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-start">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-2xl font-semibold text-foreground">Action Bundles</h2>
                </div>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Pre-built automation sequences that execute multi-step workflows across integrated systems.
                  Each bundle captures evidence and respects policy gates.
                </p>
                <div className="space-y-4">
                  {actionBundleFeatures.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-foreground">{feature.title}</div>
                        <div className="text-sm text-muted-foreground">{feature.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-2xl font-semibold text-foreground">Policy Studio</h2>
                </div>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Define, test, and deploy policies that govern when automation can execute.
                  Simulate changes before deployment.
                </p>
                <div className="space-y-4">
                  {policyStudioFeatures.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <feature.icon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-foreground">{feature.title}</div>
                        <div className="text-sm text-muted-foreground">{feature.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Template Library */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-semibold text-foreground">Template Library</h2>
              <Link
                to="/templates"
                className="inline-flex items-center gap-1 text-primary font-medium hover:underline text-sm"
              >
                View all templates
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template, idx) => (
                <div key={idx} className="bg-white border border-border rounded-lg p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="inline-flex items-center px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded">
                      {template.domain}
                    </span>
                    <span className="text-xs text-muted-foreground">{template.steps} steps</span>
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2">{template.name}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">{template.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {template.integrations.map((int, iidx) => (
                      <span key={iidx} className="text-xs text-muted-foreground bg-secondary px-2 py-0.5 rounded">
                        {int}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Versioning */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <History className="w-6 h-6 text-primary" />
                  <h2 className="text-2xl font-semibold text-foreground">Versioning & Rollback</h2>
                </div>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Every change to bundles and policies is versioned. Compare any two versions,
                  roll back instantly, and audit who changed what.
                </p>
                <div className="space-y-3">
                  {versioningFeatures.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <div>
                        <span className="text-sm font-medium text-foreground">{feature.feature}: </span>
                        <span className="text-sm text-muted-foreground">{feature.description}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-secondary/30 border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
                  <div className="text-sm font-semibold text-foreground">Version History</div>
                  <span className="text-xs text-muted-foreground">New Hire Onboarding</span>
                </div>
                <div className="space-y-3">
                  {[
                    { version: 'v3.2', author: 'Sarah Chen', change: 'Added Slack notification step', time: '2 hours ago', current: true },
                    { version: 'v3.1', author: 'Mike Johnson', change: 'Updated approval chain', time: '1 day ago' },
                    { version: 'v3.0', author: 'Sarah Chen', change: 'Added device provisioning', time: '3 days ago' },
                    { version: 'v2.9', author: 'Alex Kim', change: 'Fixed timeout handling', time: '1 week ago' }
                  ].map((v, idx) => (
                    <div key={idx} className={`flex items-center justify-between p-2 rounded ${v.current ? 'bg-primary/5 border border-primary/20' : ''}`}>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs font-mono ${v.current ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                          {v.version}
                        </span>
                        <div>
                          <div className="text-xs text-foreground">{v.change}</div>
                          <div className="text-xs text-muted-foreground">{v.author} • {v.time}</div>
                        </div>
                      </div>
                      {!v.current && (
                        <button className="text-xs text-primary hover:underline">Rollback</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-white border border-border rounded-lg p-5">
                  <h3 className="text-sm font-semibold text-foreground mb-2 flex items-start gap-2">
                    <HelpCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    {faq.question}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed pl-6">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-semibold text-foreground mb-4">Build Your First Automation</h2>
            <p className="text-muted-foreground mb-8">
              See how organizations deploy automation safely with the observe-to-auto rollout ladder.
            </p>
            <div className="flex justify-center gap-3">
              <Button asChild size="lg">
                <Link to="/demo/request">
                  Request Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/templates">Browse Templates</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default AutomationBuilder;
